import { cn } from '@resolute/ui';
import { motion, useReducedMotion } from 'framer-motion';
import { memo } from 'react';

import { type LeadershipMember } from '../leadership-data';

import { ProgressiveImage } from '@/components/progressive-image';
import { SocialIcon } from '@/components/social-icon';
import { EASE_OUT_EXPO } from '@/lib/motion';

/**
 * Leadership card component matching executive layout & website-wide motion standards:
 * - Top 4px brand accent border
 * - Profile Image (Left) with grayscale-to-color hover transition
 * - Name + LinkedIn, Designation, Description (Center)
 * - Bullet list for Key Details (Right)
 * - Subtle elevation lift using shared site-wide EASE_OUT_EXPO easing
 */
export const LeadershipCard = memo(function LeadershipCard({
  member,
  onOpen,
  className,
}: {
  member: LeadershipMember;
  onOpen: (member: LeadershipMember) => void;
  className?: string;
}) {
  const reduce = useReducedMotion();

  return (
    <motion.div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(member)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(member);
        }
      }}
      aria-haspopup="dialog"
      aria-label={`View ${member.name}'s full profile`}
      whileHover={reduce ? undefined : { y: -3 }}
      transition={{ duration: 0.2, ease: EASE_OUT_EXPO }}
      className={cn(
        'border-t-primary border-border bg-card group relative flex w-full cursor-pointer flex-col items-center justify-between gap-6 rounded-xl border border-t-4 p-6 text-center shadow-md transition-shadow duration-base ease-out-expo hover:shadow-xl sm:p-8 lg:flex-row lg:gap-10 lg:text-left',
        'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        className,
      )}
    >
      {/* 1. Left: Profile Image */}
      <div className="relative shrink-0">
        <div
          className="border-border/80 absolute inset-0 -m-1.5 rounded-full border"
          aria-hidden="true"
        />
        <ProgressiveImage
          src={member.image}
          alt={member.name}
          aspect=""
          widths={[240, 400]}
          sizes="160px"
          zoom
          className="duration-slow ease-out-expo size-32 rounded-full shadow-sm grayscale transition-[filter] group-hover:grayscale-0 sm:size-36 lg:size-44"
        />
      </div>

      {/* 2. Center: Main Information */}
      <div className="flex min-w-0 max-w-xl flex-1 flex-col items-center text-center lg:items-start lg:text-left">
        <div className="flex flex-wrap items-center justify-center gap-2.5 lg:justify-start">
          <h3 className="font-display text-foreground text-2xl font-semibold tracking-tight sm:text-3xl">
            {member.name}
          </h3>
          {member.linkedin && (
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={`${member.name} on LinkedIn`}
              className="duration-fast ease-out-expo inline-flex text-[#0A66C2] transition-transform hover:scale-110"
            >
              <SocialIcon platform="LinkedIn" className="size-5 sm:size-6" />
            </a>
          )}
        </div>
        <p className="text-secondary mt-1 text-base font-semibold sm:text-lg">
          {member.designation}
        </p>
        <p className="text-muted-foreground mt-3 text-base leading-relaxed">
          {member.bio}
        </p>
      </div>

      {/* 3. Right: Key Details Vertical Bullet List */}
      <ul className="text-muted-foreground border-border/50 w-full space-y-2 border-t pt-4 text-left text-sm leading-relaxed list-disc pl-5 lg:w-72 lg:shrink-0 lg:border-t-0 lg:pt-0">
        <li>
          <strong className="text-foreground font-semibold">Degree:</strong> {member.degree}
        </li>
        <li>
          <strong className="text-foreground font-semibold">Experience:</strong> {member.experience}
        </li>
        <li>
          <strong className="text-foreground font-semibold">Area of Work:</strong> {member.areaOfWork}
        </li>
      </ul>
    </motion.div>
  );
});



